<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-card">
        <!-- Logo -->
        <div class="auth-logo">
          <img src="/img/logo_text.png" alt="Invoice Scanner" />
        </div>

        <!-- Title -->
        <div class="auth-header">
          <h1 class="auth-title">Forgot Password?</h1>
          <p class="auth-subtitle">{{ step === 'email' ? 'Enter your email to receive a reset link' : 'Check your email for the reset link' }}</p>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="alert alert-error">
          <LineIcon name="alert-circle" :size="15" :stroke-width="1.8" />
          <span>{{ error }}</span>
        </div>

        <!-- Success Message -->
        <div v-if="success" class="alert alert-success">
          <LineIcon name="check-circle" :size="15" :stroke-width="1.8" />
          <span>{{ success }}</span>
        </div>

        <!-- Email Form (Step 1) -->
        <form v-if="step === 'email'" @submit.prevent="handleForgotPassword" class="auth-form">
          <div class="form-group">
            <label for="email" class="form-label">Email</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="form-input"
              placeholder="you@example.com"
              :disabled="loading"
            />
          </div>

          <button type="submit" class="btn-submit" :disabled="loading">
            <span v-if="loading">
              <font-awesome-icon icon="fa-solid fa-spinner" spin />
              Sending reset link...
            </span>
            <span v-else>Send Reset Link</span>
          </button>
        </form>

        <!-- Success State (Step 2) -->
        <div v-else class="success-state">
          <div class="success-icon">
            <LineIcon name="mail" :size="15" :stroke-width="1.8" />
          </div>
          <p class="success-text">
            We've sent a password reset link to <strong>{{ form.email }}</strong>.
            Please check your inbox and click the link to reset your password.
          </p>
          <p class="success-hint">
            Didn't receive the email? Check your spam folder or <button @click="handleResend" class="resend-btn" :disabled="loading">send it again</button>.
          </p>
        </div>

        <!-- Back to Login Link -->
        <div class="auth-footer">
          <router-link to="/login" class="back-link">
            <LineIcon name="arrow-left" :size="15" :stroke-width="1.8" />
            Back to Login
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const router = useRouter();

const form = ref({
  email: ''
});

const step = ref('email'); // 'email' or 'success'
const loading = ref(false);
const error = ref('');
const success = ref('');

const handleForgotPassword = async () => {
  error.value = '';
  success.value = '';
  loading.value = true;

  try {
    const response = await axios.post('/api/auth/forgot-password', {
      email: form.value.email
    });

    if (response.data.success) {
      step.value = 'success';
      success.value = 'Password reset link sent successfully!';
    }
  } catch (err) {
    if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else if (err.response?.data?.errors) {
      const errors = err.response.data.errors;
      error.value = Object.values(errors).flat().join(', ');
    } else {
      error.value = 'An error occurred. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};

const handleResend = async () => {
  error.value = '';
  success.value = '';
  loading.value = true;

  try {
    const response = await axios.post('/api/auth/forgot-password', {
      email: form.value.email
    });

    if (response.data.success) {
      success.value = 'Reset link sent again. Please check your email.';
    }
  } catch (err) {
    error.value = 'Failed to resend email. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
  padding: 20px;
}

.auth-container {
  width: 100%;
  max-width: 440px;
}

.auth-card {
  background: white;
  border-radius: 20px;
  padding: 48px 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.auth-logo {
  text-align: center;
  margin-bottom: 32px;
}

.auth-logo img {
  height: 40px;
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.auth-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.auth-subtitle {
  font-size: 15px;
  color: #64748b;
  margin: 0;
}

.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
}

.alert-error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.alert-success {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.alert svg {
  flex-shrink: 0;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  color: #1e293b;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.btn-submit {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(14, 165, 233, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.success-state {
  text-align: center;
}

.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
  border-radius: 50%;
  color: white;
  font-size: 36px;
}

.success-text {
  font-size: 15px;
  color: #334155;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.success-text strong {
  color: #0ea5e9;
  font-weight: 600;
}

.success-hint {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.resend-btn {
  background: none;
  border: none;
  color: #0ea5e9;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.resend-btn:hover:not(:disabled) {
  color: #0284c7;
}

.resend-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0ea5e9;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: color 0.2s ease;
}

.back-link:hover {
  color: #0284c7;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 32px 24px;
  }

  .auth-title {
    font-size: 24px;
  }
}
</style>
